/**
 * labelPrinter — food label ("címke") printing for the POS module.
 *
 * A `label` printer profile (backend `printerEvent`, `type: 'label'`) routes a
 * set of products to one or more paired label printers. Every ordered piece of
 * such a product gets its own sticker:
 *
 *   1. print date/time   `YYYY-MM-DD HH:mm`
 *   2. food name         bigger + bold
 *   3. label text        shelf life / allergen note (profile `labelText`)
 *
 * The layout and the printer programs live in {@link ./labelCommands} (pure).
 * This module only routes order lines to printers and pushes the bytes:
 *
 *   - `zpl` / `tspl`  raw printer language over TCP port 9100
 *     (`react-native-tcp-socket`, lazily required like
 *     {@link ../cashierTransport} does) — a network printer is mandatory.
 *   - `escpos`        receipt-style printers, rendered with the app's existing
 *     DantSu stack via `sendPrinterAndroid` (network **or** Bluetooth).
 *
 * Routing is explicit only: a label profile prints exactly the products listed
 * in its `products` array — there is no catch-all/print-everything fallback, so
 * a new food never starts printing stickers by accident. Dine-in orders and
 * pre-orders are skipped (nothing is packaged, so nothing to label).
 *
 * @module labelPrinter
 */

import {
    buildLabelCommands,
    encodeLabelBytes,
    formatLabelDateTime,
    planEscposLabel,
    resolveLabelCopies,
    resolveLabelName,
    resolveLabelSettings,
} from './labelCommands';
import {normalizeOrderType} from '../orderVatResolver';

/** Raw-printing (RAW/JetDirect) TCP port used by every label printer we ship. */
const DEFAULT_LABEL_PORT = 9100;
/** Overall connect + write timeout of one raw TCP label job. */
const DEFAULT_LABEL_TIMEOUT_MS = 8000;
/** Breather between two label jobs so a printer's buffer keeps up. */
const JOB_DELAY_MS = 50;

/**
 * Normalize any id shape to a plain string — mirrors the normalizer of
 * `triggerKitchenPrinting` (app/helpers/BackgroundTasks.js) so both print paths
 * route on identical ids (populated documents and `{$oid}` included).
 *
 * @param {*} value an id, a populated document or an extended-JSON `{$oid}`
 * @returns {string} the id, or an empty string when it cannot be resolved
 */
const normalizeId = (value) => {
    if (!value) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'number') return String(value);
    if (typeof value === 'object') return value._id || value.id || value.$oid || '';

    return '';
};

/**
 * Product id of an order line, in the same precedence the kitchen print path
 * uses (`itemId` → embedded `item` → the line's own id).
 *
 * @param {object} [item] an order line
 * @returns {string} the normalized product id
 */
const resolvePrintableProductId = (item = {}) => normalizeId(
    item?.itemId
    || item?.item?._id
    || item?.item?.id
    || item?.item
    || item?._id
    || item?.id
);

/**
 * Stable identity of a physical printer target (`ip:port` or Bluetooth MAC),
 * used to build the dedupe key of a label job.
 *
 * @param {object} [printer] a paired printer (`{ip, port}` or `{address|macAddress}`)
 * @returns {string} the target key, or an empty string when unusable
 */
const getPrinterTargetKey = (printer = {}) => {
    if (printer?.ip) {
        return `${printer.ip}:${Number(printer.port || DEFAULT_LABEL_PORT)}`;
    }

    const bluetoothAddress = printer?.address || printer?.macAddress;

    return bluetoothAddress ? String(bluetoothAddress) : '';
};

/**
 * Whether an order should get food labels at all. Dine-in is consumed on the
 * spot and a pre-order is labelled when it is actually produced, so both are
 * skipped.
 *
 * @param {object} [order] the order
 * @returns {boolean}
 */
const orderWantsLabels = (order = {}) => {
    if (!order || order.preOrder) {
        return false;
    }

    const orderType = normalizeOrderType(
        order?.orderType,
        order?.tableId || order?.tableName ? 'dineIn' : ''
    );

    return orderType !== 'dineIn';
};

/**
 * Lazily resolve the optional `react-native-tcp-socket` native module, so a
 * binary that has not been rebuilt with the dependency never crashes — the job
 * fails with a clear error instead.
 *
 * @returns {?object} the TcpSocket module, or `null` when unavailable
 */
const loadTcpSocket = () => {
    try {
        const mod = require('react-native-tcp-socket');

        return mod?.default || mod || null;
    } catch (_error) {
        return null;
    }
};

/**
 * Build every label print job of an order: one job per
 * (label profile × paired printer × matching order line).
 *
 * Pure — no IO, no storage — so the routing is unit-testable.
 *
 * @param {object} params
 * @param {object} params.order the order (`orderType`, `preOrder`, …)
 * @param {object[]} [params.items] the order lines
 * @param {object[]} [params.printerEvents] the cached `printerEvents` profiles
 * @param {Date|string|number} [params.now] print timestamp (defaults to now)
 * @returns {Array<{eventIndex: number, printerIndex: number, itemIndex: number,
 *   printer: object, settings: object, label: {date: string, name: string, text: string},
 *   copies: number, targetKey: string}>} the jobs, in print order
 */
const buildLabelJobs = ({order, items = [], printerEvents = [], now} = {}) => {
    if (!orderWantsLabels(order)) {
        return [];
    }

    const lines = Array.isArray(items) ? items : [];
    const profiles = Array.isArray(printerEvents) ? printerEvents : [];
    const labelEvents = profiles.filter((event) => event?.type === 'label');
    const date = formatLabelDateTime(now);
    const jobs = [];

    labelEvents.forEach((event, eventIndex) => {
        const products = (Array.isArray(event?.products) ? event.products : [])
            .map(normalizeId)
            .filter(Boolean);
        const printers = Array.isArray(event?.printers) ? event.printers : [];
        // Explicit routing only: no products assigned → this profile prints nothing.
        if (products.length === 0 || printers.length === 0) {
            return;
        }

        const settings = resolveLabelSettings(event);

        printers.forEach((printer, printerIndex) => {
            const printerKey = getPrinterTargetKey(printer);
            if (!printerKey) {
                return;
            }

            lines.forEach((item, itemIndex) => {
                if (!products.includes(resolvePrintableProductId(item))) {
                    return;
                }

                jobs.push({
                    eventIndex,
                    printerIndex,
                    itemIndex,
                    printer,
                    settings,
                    label: {
                        date,
                        name: resolveLabelName(item),
                        text: settings.text,
                    },
                    copies: resolveLabelCopies(item),
                    targetKey: `label:${eventIndex}:${printerIndex}:${itemIndex}:${printerKey}`,
                });
            });
        });
    });

    return jobs;
};

/**
 * One sample label job for the printer-profile editor's "test label" button.
 *
 * The printer is resolved exactly like {@link buildLabelJobs} does it: the
 * first paired printer of the profile with a usable target (network `ip` or
 * Bluetooth `address`/`macAddress`). The profile may carry unsaved editor
 * values (`labelSize`, `labelRotation`, …) — they are resolved with the same
 * `resolveLabelSettings` as a real order. Product routing and the order type
 * are deliberately ignored: a test label always prints exactly one copy.
 *
 * @param {object} params
 * @param {object} params.event the label printer profile (possibly unsaved values)
 * @param {string} params.name the sample product name printed in bold
 * @param {Date|string|number} [params.now] print timestamp (defaults to now)
 * @returns {?{eventIndex: number, printerIndex: number, itemIndex: number,
 *   printer: object, settings: object, label: {date: string, name: string, text: string},
 *   copies: number, targetKey: string}} the job, or `null` when no printer is paired
 */
const buildTestLabelJob = ({event, name, now} = {}) => {
    const printers = Array.isArray(event?.printers) ? event.printers : [];
    const printerIndex = printers.findIndex((printer) => Boolean(getPrinterTargetKey(printer)));
    if (printerIndex === -1) {
        return null;
    }

    const printer = printers[printerIndex];
    const settings = resolveLabelSettings(event);

    return {
        eventIndex: -1,
        printerIndex,
        itemIndex: -1,
        printer,
        settings,
        label: {
            date: formatLabelDateTime(now),
            name: typeof name === 'string' ? name : '',
            text: settings.text,
        },
        copies: 1,
        targetKey: `label:test:${printerIndex}:${getPrinterTargetKey(printer)}`,
    };
};

/**
 * Push raw printer bytes to a network label printer over TCP (port 9100).
 *
 * Mirrors the socket lifecycle of {@link ../cashierTransport}: resolve on
 * close, reject on error/timeout, always destroy the socket.
 *
 * @param {object} params
 * @param {string} params.ip printer IP / hostname
 * @param {(number|string)} [params.port=9100] TCP port
 * @param {Uint8Array} params.bytes the encoded printer program
 * @param {number} [params.timeoutMs=8000] overall connect + write timeout
 * @returns {Promise<boolean>} resolves `true` once the bytes are flushed
 */
const sendRawTcp = ({ip, port, bytes, timeoutMs} = {}) => new Promise((resolve, reject) => {
    const TcpSocket = loadTcpSocket();
    if (!TcpSocket || typeof TcpSocket.createConnection !== 'function') {
        reject(new Error('LABEL_TRANSPORT_UNAVAILABLE'));

        return;
    }

    const host = ip;
    const targetPort = Number(port || DEFAULT_LABEL_PORT);
    const timeout = Number(timeoutMs || DEFAULT_LABEL_TIMEOUT_MS);

    if (!host || !targetPort) {
        reject(new Error('LABEL_PRINTER_NOT_CONFIGURED'));

        return;
    }

    let settled = false;
    let client = null;
    let timer = null;

    const finish = (error) => {
        if (settled) {
            return;
        }
        settled = true;
        if (timer) {
            globalThis.clearTimeout(timer);
            timer = null;
        }
        try {
            if (client && typeof client.destroy === 'function') {
                client.destroy();
            }
        } catch (_error) { /* ignore – socket already closed */ }

        if (error) {
            reject(error instanceof Error ? error : new Error(String(error)));
        } else {
            resolve(true);
        }
    };

    timer = globalThis.setTimeout(() => finish(new Error('LABEL_PRINT_TIMEOUT')), timeout);

    try {
        client = TcpSocket.createConnection({host, port: targetPort, tls: false}, () => {
            try {
                // Buffer is not guaranteed in the RN runtime — fall back to the
                // raw Uint8Array, which the native module accepts as well.
                const BufferImpl = globalThis.Buffer;
                const payload = BufferImpl && typeof BufferImpl.from === 'function'
                    ? BufferImpl.from(bytes)
                    : bytes;
                client.write(payload);
            } catch (writeError) {
                finish(writeError);

                return;
            }
            if (typeof client.end === 'function') {
                client.end();
            }
            // Give the socket a beat to flush the bytes before we close it.
            globalThis.setTimeout(() => finish(null), 400);
        });

        client.on('error', (socketError) => finish(socketError));
        client.on('close', (hadError) => {
            if (!hadError) {
                finish(null);
            }
        });
    } catch (connectError) {
        finish(connectError);
    }
});

/**
 * ESC/POS payload of a single label in the DantSu formatted-text syntax used by
 * the rest of the app (see {@link ./courierLabelPrinter}).
 *
 * @param {object} plan output of `planEscposLabel`
 * @returns {string} the formatted payload
 */
const buildEscposLabelPayload = (plan = {}) => {
    const nameLines = Array.isArray(plan.nameLines) ? plan.nameLines : [];
    const textLines = Array.isArray(plan.textLines) ? plan.textLines : [];

    return (
        `[C]${plan.date || ''}\n`
        + nameLines.map((line) => `[C]<font size='big'><b>${line}</b></font>\n`).join('')
        + textLines.map((line) => `[C]${line}\n`).join('')
        + '[C]\n'
    );
};

/**
 * Print one label job.
 *
 * `zpl`/`tspl` are sent as raw bytes over TCP — the copy count is part of the
 * program itself (`^PQ` / `PRINT 1,n`) — so they need a network printer.
 * `escpos` is rendered by the app's DantSu stack, once per copy, and also works
 * over Bluetooth.
 *
 * @param {object} job a job from {@link buildLabelJobs}
 * @param {object} deps
 * @param {function} deps.sendPrinterAndroid the app's ESC/POS sender
 * @param {function} [deps.sendRaw=sendRawTcp] raw TCP sender (injectable)
 * @returns {Promise<true>} resolves `true` when the label(s) printed
 * @throws {Error} `labelRawNeedsNetworkPrinter` for a raw job on a Bluetooth-only
 *   printer, or the underlying transport/printer error
 */
const printLabelJob = async (job, {sendPrinterAndroid, sendRaw = sendRawTcp} = {}) => {
    const settings = job?.settings || {};
    const label = job?.label || {};
    const printer = job?.printer || {};
    const copies = Math.max(1, Number(job?.copies) || 1);

    if (settings.language === 'escpos') {
        if (typeof sendPrinterAndroid !== 'function') {
            throw new Error('labelEscposSenderMissing');
        }

        const plan = planEscposLabel({
            widthMm: settings.widthMm,
            heightMm: settings.heightMm,
            date: label.date,
            name: label.name,
            text: label.text,
        });
        const payload = buildEscposLabelPayload(plan);
        const bluetoothAddress = printer.address || printer.macAddress;
        const config = printer.ip
            ? {
                ip: printer.ip,
                port: Number(printer.port || DEFAULT_LABEL_PORT),
                timeout: 400,
                autoCut: true,
                mmFeedPaper: settings.heightMm,
                printerDpi: settings.dpi,
                printerWidthMM: settings.widthMm,
                printerNbrCharactersPerLine: plan.charsPerLine,
            }
            : {
                macAddress: bluetoothAddress,
                autoCut: true,
                mmFeedPaper: settings.heightMm,
                printerDpi: settings.dpi,
                printerWidthMM: settings.widthMm,
                printerNbrCharactersPerLine: plan.charsPerLine,
            };

        if (!printer.ip && !bluetoothAddress) {
            throw new Error('labelPrinterNotConfigured');
        }

        for (let copy = 0; copy < copies; copy += 1) {
            // sendPrinterAndroid resolves with `true` or an {error} descriptor.
            const result = await sendPrinterAndroid(payload, config, true, false);
            if (result !== true) {
                throw (result?.error instanceof Error ? result.error : new Error('labelEscposPrintFailed'));
            }
        }

        return true;
    }

    if (!printer.ip) {
        // ZPL/TSPL are raw printer languages: they need the raw TCP channel.
        throw new Error('labelRawNeedsNetworkPrinter');
    }

    const commands = buildLabelCommands({
        ...settings,
        ...label,
        copies,
    });
    if (!commands) {
        throw new Error('labelCommandsUnavailable');
    }

    const send = typeof sendRaw === 'function' ? sendRaw : sendRawTcp;
    await send({
        ip: printer.ip,
        port: Number(printer.port || DEFAULT_LABEL_PORT),
        bytes: encodeLabelBytes(commands.program, commands.encoding),
        timeoutMs: DEFAULT_LABEL_TIMEOUT_MS,
    });

    return true;
};

/**
 * Print every food label of an order.
 *
 * One dead printer never stops the others: each job is caught individually and
 * only counted. `printedTargets` (shared across retries of the same print job)
 * makes a retry skip what already printed, so no sticker is duplicated.
 *
 * @param {object} params
 * @param {object} params.order the order
 * @param {object[]} [params.items] the order lines
 * @param {object[]} [params.printerEvents] the cached `printerEvents` profiles
 * @param {function} params.sendPrinterAndroid the app's ESC/POS sender
 * @param {?Set<string>} [params.printedTargets] already-printed target keys
 * @param {function} [params.sendRaw] raw TCP sender (injectable for tests)
 * @returns {Promise<{printed: number, failed: number}>} per-job outcome counters
 */
const printOrderLabels = async ({
    order,
    items = [],
    printerEvents = [],
    sendPrinterAndroid,
    printedTargets = null,
    sendRaw = sendRawTcp,
} = {}) => {
    const jobs = buildLabelJobs({order, items, printerEvents});
    let printed = 0;
    let failed = 0;

    for (const job of jobs) {
        if (printedTargets?.has(job.targetKey)) {
            continue;
        }
        try {
            await printLabelJob(job, {sendPrinterAndroid, sendRaw});
            printedTargets?.add(job.targetKey);
            printed += 1;
        } catch (jobError) {
            failed += 1;
            console.error('Label printer failed:', job.targetKey, jobError?.message || jobError);
        }
        await new Promise((resolve) => globalThis.setTimeout(resolve, JOB_DELAY_MS));
    }

    return {printed, failed};
};

export {
    DEFAULT_LABEL_PORT,
    buildLabelJobs,
    buildTestLabelJob,
    buildEscposLabelPayload,
    sendRawTcp,
    printLabelJob,
    printOrderLabels,
};
